import { useState, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Sparkles, Upload, RefreshCw, XCircle, FileText, X, Zap,
  AlertTriangle, ChevronDown, ChevronUp, CalendarX,
} from "lucide-react";

const MONTH_NAMES = ["","janeiro","fevereiro","março","abril","maio","junho","julho","agosto","setembro","outubro","novembro","dezembro"];
function humanMonth(ym) {
  if (!ym) return ym;
  const [y, m] = ym.split("-");
  return `${MONTH_NAMES[parseInt(m)] || m} de ${y}`;
}

const MAX_FILES = 10;

// ─── InvoiceCard ────────────────────────────────────────────────────────────

function InvoiceCard({ result, categories, onToggle, currentMonth }) {
  const [open, setOpen] = useState(false);
  const matched = (result.items || []).filter(it => it.stock_id).length;
  const isWrongMonth = currentMonth && result.date && !result.date.startsWith(currentMonth);
  const invoiceMonth = result.date ? result.date.slice(0, 7) : null;

  return (
    <div className={`p-3 transition-colors ${result.selected ? "bg-card" : "bg-muted/20 opacity-55"}`}>
      {isWrongMonth && result.selected && (
        <div className="flex items-start gap-2 mb-2 px-1 py-1.5 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
          <CalendarX className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-red-500" />
          <span>
            Esta fatura tem data de <strong>{humanMonth(invoiceMonth)}</strong>, mas está a lançar em <strong>{humanMonth(currentMonth)}</strong>.
            Se salvar assim, vai alterar os resultados de {humanMonth(invoiceMonth)}.
          </span>
        </div>
      )}
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={result.selected}
          onChange={onToggle}
          className="w-4 h-4 accent-violet-600 mt-0.5 flex-shrink-0 cursor-pointer"
        />

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-sm">{result.supplier_name || "Fornecedor desconhecido"}</span>
            {result.invoice_number && (
              <span className="text-xs text-muted-foreground">#{result.invoice_number}</span>
            )}
            {result.duplicate && (
              <Badge className="bg-amber-100 text-amber-700 text-[10px] gap-1 py-0">
                <AlertTriangle className="w-2.5 h-2.5" />já existe
              </Badge>
            )}
            {isWrongMonth && (
              <Badge className="bg-red-100 text-red-700 text-[10px] gap-1 py-0">
                <CalendarX className="w-2.5 h-2.5" />mês diferente
              </Badge>
            )}
          </div>

          {/* Meta row */}
          <div className="flex flex-wrap gap-2 mt-0.5 text-xs text-muted-foreground">
            {result.date && <span>{result.date}</span>}
            {categories?.[result.category] && (
              <span className="bg-muted px-1.5 py-0.5 rounded font-medium">{categories[result.category]}</span>
            )}
            {matched > 0 && (
              <span className="text-violet-600 font-medium">{matched} ingrediente{matched !== 1 ? "s" : ""} ✓</span>
            )}
          </div>

          {/* Items toggle */}
          {result.items?.length > 0 && (
            <div className="mt-1.5">
              <button
                onClick={() => setOpen(v => !v)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {result.items.length} item{result.items.length !== 1 ? "s" : ""}
              </button>

              {open && (
                <div className="mt-1.5 rounded-lg bg-muted/40 divide-y divide-border/20 text-xs">
                  {result.items.map((it, i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-1.5">
                      <span className={`w-3 text-center ${it.stock_id ? "text-violet-500" : "text-transparent"}`}>✓</span>
                      <span className="flex-1 font-medium truncate">{it.name}</span>
                      <span className="text-muted-foreground tabular-nums flex-shrink-0">
                        {it.quantity} {it.unit} × {it.price?.toFixed(2)}€
                      </span>
                      <span className="font-bold tabular-nums flex-shrink-0 w-16 text-right">
                        {((it.quantity || 1) * (it.price || 0)).toFixed(2)}€
                      </span>
                    </div>
                  ))}
                  {result.tax_amount > 0 && (
                    <div className="flex justify-between px-2 py-1 text-muted-foreground">
                      <span>IVA {result.tax_rate}%</span>
                      <span>{result.tax_amount?.toFixed(2)}€</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Total */}
        <span className="font-black text-base tabular-nums flex-shrink-0 mt-0.5">
          {result.total?.toFixed(2)}€
        </span>
      </div>
    </div>
  );
}

// ─── AIBatchImportDrawer ─────────────────────────────────────────────────────

export function AIBatchImportDrawer({ open, onClose, categories, onSuccess, month }) {
  const { authAxios } = useAuth();

  // files: [{id, file, status, error, count}]
  const [files, setFiles] = useState([]);
  // results: flat list of all extracted invoices across all files
  const [results, setResults] = useState([]);
  const [expandedFiles, setExpandedFiles] = useState(new Set());
  const [updateCosts, setUpdateCosts] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dateConflict, setDateConflict] = useState(null); // { toCreate, wrongCount, wrongMonth }
  const fileInputRef = useRef(null);

  const _sig = (doc) =>
    `${(doc.supplier_name || "").toLowerCase()}|${doc.invoice_number || ""}|${doc.total || 0}|${doc.date || ""}`;

  const processFile = useCallback(async (entry) => {
    setFiles(prev => prev.map(f => f.id === entry.id ? { ...f, status: "processing" } : f));
    try {
      const fd = new FormData();
      fd.append("file", entry.file);
      const res = await authAxios.post("/financiero/bills/extract-ai", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
        _noRetry: true,
      });
      if (res.data.success) {
        const docs = res.data.documents || [];
        const newResults = docs.map((doc, idx) => ({
          _key: `${entry.id}-${idx}`,
          _fileId: entry.id,
          selected: true,
          ...doc,
        }));
        setResults(prev => {
          const existingSigs = new Set(prev.map(_sig));
          return [
            ...prev,
            ...newResults.map(r => ({ ...r, duplicate: r.duplicate || existingSigs.has(_sig(r)) })),
          ];
        });
        setFiles(prev => prev.map(f =>
          f.id === entry.id ? { ...f, status: "done", count: docs.length } : f
        ));
        setExpandedFiles(prev => new Set([...prev, entry.id]));
      } else {
        setFiles(prev => prev.map(f =>
          f.id === entry.id ? { ...f, status: "error", error: res.data.error } : f
        ));
      }
    } catch {
      setFiles(prev => prev.map(f =>
        f.id === entry.id ? { ...f, status: "error", error: "Erro de conexão" } : f
      ));
    }
  }, [authAxios]);

  const addFiles = useCallback((rawFiles) => {
    const allowed = Array.from(rawFiles).slice(0, MAX_FILES - files.length);
    const entries = allowed.map(file => ({
      id: Math.random().toString(36).slice(2),
      file,
      status: "pending",
      error: null,
      count: 0,
    }));
    setFiles(prev => [...prev, ...entries]);
    entries.forEach(e => processFile(e));
  }, [files.length, processFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const toggleResult = (key) =>
    setResults(prev => prev.map(r => r._key === key ? { ...r, selected: !r.selected } : r));

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
    setResults(prev => prev.filter(r => r._fileId !== fileId));
    setExpandedFiles(prev => { const n = new Set(prev); n.delete(fileId); return n; });
  };

  const toggleExpand = (fileId) =>
    setExpandedFiles(prev => {
      const n = new Set(prev);
      n.has(fileId) ? n.delete(fileId) : n.add(fileId);
      return n;
    });

  const _doCreate = async (toCreate, dateOverride) => {
    setSubmitting(true);
    setDateConflict(null);
    let created = 0;
    let costUpdates = [];
    let suppliersCreated = 0;
    let stockCreated = 0;
    let entriesCreated = 0;

    for (const doc of toCreate) {
      const docDate = dateOverride && month && doc.date && !doc.date.startsWith(month)
        ? `${month}-${doc.date.slice(8)}`
        : (doc.date || new Date().toISOString().slice(0, 10));
      const billItems = (doc.items || []).map(it => ({
        name: it.name || "",
        quantity: it.quantity || 1,
        price: it.price || 0,
        unit: it.unit || "",
        category: it.category || "",
      }));
      try {
        const res = await authAxios.post("/financiero/bills", {
          supplier_name: doc.supplier_name || "",
          invoice_number: doc.invoice_number || "",
          category: doc.category || "outros",
          date: docDate,
          due_date: doc.due_date || "",
          notes: doc.notes || "",
          items: billItems,
          declared_total: doc.total,
          status: "pending",
        });
        created++;
        if (res.data.supplier_created) suppliersCreated++;
        if (res.data.stock_items_created > 0) stockCreated += res.data.stock_items_created;
        if (res.data.stock_entries_created > 0) entriesCreated += res.data.stock_entries_created;
        if (updateCosts) {
          (doc.items || [])
            .filter(it => it.stock_id && it.new_cost > 0)
            .forEach(it => costUpdates.push({ stock_id: it.stock_id, new_cost: it.new_cost }));
        }
      } catch {}
    }

    if (costUpdates.length > 0) {
      try {
        await authAxios.post("/financiero/bills/update-stock-costs", { updates: costUpdates });
      } catch {}
    }

    const costMsg = costUpdates.length > 0 ? ` · ${costUpdates.length} custo(s) atualizados` : "";
    toast.success(`${created} fatura(s) lançadas no DRE${costMsg}`);
    if (suppliersCreated > 0) toast.success(`${suppliersCreated} fornecedor(es) registado(s) automaticamente`);
    if (stockCreated > 0) toast.success(`${stockCreated} ingrediente(s) criado(s) no inventário`);
    if (entriesCreated > 0) toast.success(`Stock atualizado: ${entriesCreated} entrada(s) de mercadoria registada(s)`);
    setFiles([]); setResults([]); setExpandedFiles(new Set());
    setSubmitting(false);
    onSuccess?.();
    onClose();
  };

  const handleConfirm = async () => {
    const toCreate = results.filter(r => r.selected);
    if (!toCreate.length) { toast.error("Selecione pelo menos uma fatura"); return; }

    const wrongMonthDocs = month
      ? toCreate.filter(doc => doc.date && !doc.date.startsWith(month))
      : [];

    if (wrongMonthDocs.length > 0) {
      const wrongMonth = wrongMonthDocs[0].date.slice(0, 7);
      setDateConflict({ toCreate, wrongCount: wrongMonthDocs.length, wrongMonth });
      return;
    }

    await _doCreate(toCreate, false);
  };

  const handleClose = () => {
    if (submitting) return;
    setFiles([]); setResults([]); setExpandedFiles(new Set()); setDateConflict(null);
    onClose();
  };

  const selectedCount = results.filter(r => r.selected).length;
  const processingCount = files.filter(f => f.status === "processing").length;
  const hasIngredientMatches = results.some(r =>
    r.selected && (r.items || []).some(it => it.stock_id)
  );

  // Group results by file for rendering
  const byFile = {};
  results.forEach(r => { (byFile[r._fileId] = byFile[r._fileId] || []).push(r); });

  return (
    <>
    <Dialog open={open} onOpenChange={o => !o && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-600" />
            Importar Faturas com IA
            <span className="text-xs font-normal text-muted-foreground ml-1">
              até {MAX_FILES} ficheiros · múltiplas faturas por ficheiro
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Drop zone */}
        {files.length < MAX_FILES && (
          <div
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-violet-300 rounded-xl p-8 text-center cursor-pointer hover:bg-violet-50 transition-colors select-none"
          >
            <Upload className="w-8 h-8 text-violet-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-violet-700">Arrasta PDFs ou fotos aqui</p>
            <p className="text-xs text-muted-foreground mt-1">
              ou clica para selecionar · PDF, JPG, PNG, WEBP · máx. {MAX_FILES} ficheiros
            </p>
            <p className="text-xs text-violet-500 mt-1 font-medium">
              ✓ Suporta múltiplas faturas num único PDF
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="application/pdf,image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={e => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ""; }}
            />
          </div>
        )}

        {/* File groups */}
        {files.length > 0 && (
          <div className="space-y-3 mt-1">
            {files.map(f => (
              <div key={f.id} className="rounded-lg border border-border/60 overflow-hidden">

                {/* File header */}
                <div
                  className="flex items-center gap-3 px-3 py-2 bg-muted/40 cursor-pointer hover:bg-muted/60 transition-colors select-none"
                  onClick={() => f.status === "done" && toggleExpand(f.id)}
                >
                  {f.status === "processing"
                    ? <RefreshCw className="w-4 h-4 text-violet-500 animate-spin flex-shrink-0" />
                    : f.status === "error"
                    ? <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    : <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />}

                  <span className="text-xs font-medium text-muted-foreground flex-1 truncate">
                    {f.file.name}
                  </span>

                  {f.status === "processing" && (
                    <span className="text-xs text-violet-600">A analisar...</span>
                  )}
                  {f.status === "error" && (
                    <span className="text-xs text-red-500">{f.error}</span>
                  )}
                  {f.status === "done" && (
                    <div className="flex items-center gap-2">
                      <Badge className="bg-violet-100 text-violet-700 text-[10px]">
                        {f.count} fatura{f.count !== 1 ? "s" : ""}
                      </Badge>
                      {expandedFiles.has(f.id)
                        ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
                        : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
                    </div>
                  )}

                  <button
                    onClick={e => { e.stopPropagation(); removeFile(f.id); }}
                    className="text-muted-foreground hover:text-red-500 transition-colors ml-1 p-0.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Invoice cards */}
                {f.status === "done" && expandedFiles.has(f.id) && (
                  <div className="divide-y divide-border/30">
                    {(byFile[f.id] || []).map(r => (
                      <InvoiceCard
                        key={r._key}
                        result={r}
                        categories={categories}
                        onToggle={() => toggleResult(r._key)}
                        currentMonth={month}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Update costs toggle */}
        {hasIngredientMatches && (
          <label className="flex items-center gap-2 text-sm cursor-pointer bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
            <input
              type="checkbox"
              checked={updateCosts}
              onChange={e => setUpdateCosts(e.target.checked)}
              className="w-4 h-4 accent-violet-600"
            />
            <span className="text-violet-800 font-medium">Atualizar custos nas fichas técnicas</span>
          </label>
        )}

        <div className="flex justify-between items-center pt-2">
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            className="bg-violet-600 hover:bg-violet-700 text-white gap-2"
            disabled={selectedCount === 0 || submitting || processingCount > 0}
            onClick={handleConfirm}
          >
            {submitting
              ? <RefreshCw className="w-4 h-4 animate-spin" />
              : <Zap className="w-4 h-4" />}
            {submitting
              ? "A criar..."
              : selectedCount === 0
                ? "Nenhuma fatura detectada"
                : `Criar ${selectedCount} Fatura${selectedCount !== 1 ? "s" : ""} no DRE`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Date conflict confirmation dialog */}
    <Dialog open={!!dateConflict} onOpenChange={o => !o && setDateConflict(null)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <CalendarX className="w-5 h-5" />
            Atenção: fatura{dateConflict?.wrongCount !== 1 ? "s" : ""} com data de mês anterior
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-gray-700 space-y-2">
          <p>
            <strong>{dateConflict?.wrongCount} fatura{dateConflict?.wrongCount !== 1 ? "s" : ""}</strong> têm data de{" "}
            <strong className="text-red-700">{humanMonth(dateConflict?.wrongMonth)}</strong>, mas está a lançar no mês de{" "}
            <strong>{humanMonth(month)}</strong>.
          </p>
          <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Se manter a data original, os resultados de <strong>{humanMonth(dateConflict?.wrongMonth)}</strong> serão alterados.
          </p>
          <p>Deseja manter a data original ou actualizar para {humanMonth(month)}?</p>
        </div>
        <DialogFooter className="flex gap-2 sm:flex-col">
          <Button
            variant="outline"
            className="border-red-200 text-red-700 hover:bg-red-50 flex-1"
            onClick={() => _doCreate(dateConflict.toCreate, false)}
            disabled={submitting}
          >
            Manter data original ({humanMonth(dateConflict?.wrongMonth)})
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white flex-1"
            onClick={() => _doCreate(dateConflict.toCreate, true)}
            disabled={submitting}
          >
            {submitting ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : null}
            Actualizar para {humanMonth(month)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
